
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Users, ShieldX } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { UserTable } from './components/UserTable';
import { UserFilters } from './components/UserFilters';
import { UserDetailsDialog } from './components/dialog/UserDetailsDialog';
import { UserEditDialog } from './components/dialog/UserEditDialog';
import { Pagination } from './components/Pagination';
import { useUserManagement } from './hooks/useUserManagement';
import { Skeleton } from "@/components/ui/skeleton";

export const UserManagement = () => {
  const {
    users,
    loading,
    isAdmin,
    currentPage,
    totalPages,
    searchQuery,
    sortBy,
    sortOrder,
    filterVerified,
    filterType,
    selectedUser,
    userDetailsOpen,
    editUserOpen,
    userProducts,
    loadingProducts,
    hardcodedAdminId,
    setSearchQuery,
    setSortBy,
    setSortOrder,
    setFilterVerified,
    setFilterType,
    setCurrentPage,
    confirmDeleteUser,
    handleDeleteUser,
    toggleAdminRole,
    toggleVerifiedStatus,
    toggleBlockedStatus,
    checkAdminStatus,
    handleSort,
    openUserDetails,
    openEditUser,
    updateUserProfile,
    deleteUserProduct,
    setUserDetailsOpen,
    setEditUserOpen,
    deleteDialogOpen,
    setDeleteDialogOpen,
    userToDelete,
    deletingUser
  } = useUserManagement();

  if (loading && !users.length) {
    return (
      <div className="space-y-4 p-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Users className="h-6 w-6" /> Gestion des utilisateurs
        </h1>
        
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex items-center gap-4 mb-4">
                <Skeleton className="h-10 w-56" />
                <Skeleton className="h-10 w-32" />
                <Skeleton className="h-10 w-32" />
              </div>
              
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <div className="flex flex-col items-center justify-center p-8">
            <ShieldX className="h-12 w-12 text-red-500 mb-4" />
            <p className="text-lg font-medium">Accès refusé</p>
            <p className="text-muted-foreground">Vous n'avez pas les permissions requises pour accéder à cette page.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <h1 className="text-2xl font-bold flex items-center gap-2">
        <Users className="h-6 w-6" /> Gestion des utilisateurs
      </h1>
      
      <UserFilters 
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filterType={filterType}
        setFilterType={setFilterType}
        filterVerified={filterVerified}
        setFilterVerified={setFilterVerified}
      />
      
      <UserTable 
        users={users}
        sortBy={sortBy}
        sortOrder={sortOrder}
        handleSort={handleSort}
        confirmDeleteUser={confirmDeleteUser}
        toggleVerifiedStatus={toggleVerifiedStatus}
        toggleBlockedStatus={toggleBlockedStatus}
        checkAdminStatus={checkAdminStatus}
        toggleAdminRole={toggleAdminRole}
        openUserDetails={openUserDetails}
        openEditUser={openEditUser}
        hardcodedAdminId={hardcodedAdminId}
      />
      
      {totalPages > 1 && (
        <Pagination 
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
        />
      )}

      <UserDetailsDialog 
        open={userDetailsOpen}
        setOpen={setUserDetailsOpen}
        user={selectedUser}
        products={userProducts}
        loadingProducts={loadingProducts}
        checkAdminStatus={checkAdminStatus}
        openEditUser={openEditUser}
        deleteUserProduct={deleteUserProduct}
        confirmDeleteUser={confirmDeleteUser}
      />

      <UserEditDialog
        open={editUserOpen}
        setOpen={setEditUserOpen}
        user={selectedUser}
        onSave={updateUserProfile}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Confirmation de suppression
            </AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est définitive et supprimera toutes les données associées à ce compte.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingUser}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => userToDelete && handleDeleteUser(userToDelete)}
              disabled={deletingUser}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deletingUser ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-t-transparent rounded-full"></div>
                  Suppression...
                </div>
              ) : (
                "Supprimer"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
